/**
 * Client list search predicate.
 *
 * WHY THIS IS ITS OWN MODULE
 *
 * This logic used to be inlined twice inside `ClientList`'s `useMemo` filters as
 *
 *     c.first_name.toLowerCase().includes(q) ||
 *     c.last_name.toLowerCase().includes(q) ||
 *     c.email.toLowerCase().includes(q)
 *
 * and the `c.email` call was UNGUARDED.
 *
 * CORRECTED 2026-08-06. This comment previously said the unguarded call was
 * "safe only while `clients.email` is NOT NULL" and framed the fix as groundwork
 * for a future migration. **That was false.** Source of truth is the catalog:
 *
 *     SELECT column_name, is_nullable FROM information_schema.columns
 *     WHERE table_schema='public' AND table_name='clients' AND column_name='email';
 *     -> is_nullable = YES
 *
 * `clients.email` is **already nullable in production**, so nothing prevents a
 * null from reaching this filter today — this is a live defect fix, not
 * preparation. The original claim traced to `docs/database/supabase_schema.sql`,
 * which is NOT evidence about production: in a single day it mis-stated a table's
 * absence, a column's existence, and a column's nullability. **Query the catalog,
 * never that dump.**
 *
 * (Measured alongside: 126 clients, 0 with a null email — so no outage had begun.
 * A guard on a real gap, not a fix to a firing incident. The correction stands
 * independently of that count, because the constraint claimed to prevent it does
 * not exist.)
 *
 * The failure mode is much worse than a row that does not match. The call sits
 * inside a filter over the WHOLE list, so a single client with no email throws
 * `TypeError: Cannot read properties of null (reading 'toLowerCase')` while
 * filtering, and **client search breaks for every client** — a whole-page
 * outage from one record, on the main page of the CRM.
 *
 * Extracted rather than patched in place so the predicate is directly testable:
 * a render test would have to reproduce the whole list to reach it, and the bug
 * is in one boolean expression.
 *
 * Ships AHEAD of the migration on purpose. It is R1, it depends on nothing, and
 * it shrinks that migration's blast radius before anyone writes it. Fixing this
 * after the column became nullable would mean fixing it during the outage.
 */

/**
 * Lowercase a value that the database may not actually guarantee.
 *
 * `?? ""` and not `String(value)`: a null must become the empty string (matching
 * nothing), never the literal `"null"`, which would make a client with no email
 * match a search for "null".
 */
function searchable(value: string | null | undefined): string {
  return (value ?? "").toLowerCase();
}

/**
 * True when the client matches the operator's search box.
 *
 * `query` is expected pre-lowercased by the caller (it is computed once per
 * keystroke rather than once per row). An empty query matches everything, which
 * is what `String.includes("")` already does — callers skip filtering entirely
 * in that case, so this is defensive rather than load-bearing.
 *
 * All three fields are guarded, not just `email`. I have **not** verified
 * `first_name`/`last_name` nullability against the catalog, so this deliberately
 * makes no claim about it — which is the point: the guard costs nothing, holds
 * either way, and means the next column relaxed cannot reintroduce this outage in
 * this expression. A guard that does not depend on a schema claim cannot be
 * invalidated by one being wrong, and in this file one already was.
 */
export function clientMatchesSearch(
  client: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  },
  query: string
): boolean {
  return (
    searchable(client.first_name).includes(query) ||
    searchable(client.last_name).includes(query) ||
    searchable(client.email).includes(query)
  );
}
